import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
  useTheme,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PageHeader from '../components/common/PageHeader';
import FormSkeleton from '../components/common/FormSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HistorieTimeline from '../components/common/HistorieTimeline';
import { MitarbeiterProfilReadOnlyCard } from '../components/common/MitarbeiterProfilReadOnlyCard';
import { mitarbeiterService } from '../services/mitarbeiterService';
import { rollenService } from '../services/lookupService';
import {
  benachrichtigungenService,
  BenachrichtigungenAdminResponse,
  BenachrichtigungenUpdateAdmin,
  EventDefinition,
} from '../services/benachrichtigungenService';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { useNotification } from '../hooks/NotificationContext';
import { Mitarbeiter, MitarbeiterCreate, Rolle } from '../types';
import { validatePasswordPolicy } from '../utils/passwordPolicy';

const MitarbeiterDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'neu';

  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter | null>(null);
  const [rollen, setRollen] = useState<Rolle[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reset2faDialogOpen, setReset2faDialogOpen] = useState(false);
  const [reset2faLoading, setReset2faLoading] = useState(false);
  const [forcePasswordResetDialogOpen, setForcePasswordResetDialogOpen] = useState(false);
  const [forcePasswordResetLoading, setForcePasswordResetLoading] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [benachrichtigungen, setBenachrichtigungen] = useState<BenachrichtigungenAdminResponse | null>(null);
  const [benachrichtigungenLoading, setBenachrichtigungenLoading] = useState(false);
  const [benachrichtigungenSaving, setBenachrichtigungenSaving] = useState(false);
  const [gotifyToken, setGotifyToken] = useState('');
  const [gotifyUrl, setGotifyUrl] = useState('');
  const [notificationEvents, setNotificationEvents] = useState<EventDefinition[]>([]);
  const [overrideEventsEmail, setOverrideEventsEmail] = useState<string[]>([]);
  const [overrideEventsGotify, setOverrideEventsGotify] = useState<string[]>([]);
  const { hasRole, hasPermission } = usePermissions();
  const { showSuccess } = useNotification();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;

  const [formData, setFormData] = useState<MitarbeiterCreate>({
    vorname: '',
    nachname: '',
    email: '',
    aktiv: true,
    bleeding_edge: false,
    rollen_ids: [],
    urlaubstage_gesamt: 30,
    eintrittsdatum: '',
    urlaub_uebertrag: 0,
    password: '',
  } as any);
  const passwordPolicy = useMemo(() => validatePasswordPolicy(formData.password || ''), [formData.password]);

  useEffect(() => {
    const loadRollen = async () => {
      try {
        const data = await rollenService.list();
        setRollen(data);
      } catch {
        // Rollen laden fehlgeschlagen – nicht kritisch
      }
    };
    loadRollen();

    if (!isNew && id) {
      const loadMitarbeiter = async () => {
        try {
          setLoading(true);
          const data = await mitarbeiterService.get(parseInt(id));
          setMitarbeiter(data);
          setFormData({
            vorname: data.vorname,
            nachname: data.nachname,
            email: data.email,
            aktiv: data.aktiv,
            bleeding_edge: data.bleeding_edge,
            rollen_ids: data.rollen?.map((r) => r.id) || [],
            urlaubstage_gesamt: data.urlaubstage_gesamt,
            eintrittsdatum: data.eintrittsdatum ?? '',
            urlaub_uebertrag: data.urlaub_uebertrag ?? 0,
            password: '', // Don't populate password for existing users
          });
        } catch {
          setError('Fehler beim Laden des Mitarbeiters');
        } finally {
          setLoading(false);
        }
      };
      loadMitarbeiter();
    }
  }, [id, isNew]);

  const canEditBenachrichtigungen = hasPermission(P.MITARBEITER_EDIT) || hasPermission(P.VERWALTUNG_MANAGE);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const mid = id ? parseInt(id, 10) : NaN;
        const params =
          !isNaN(mid) && canEditBenachrichtigungen
            ? { filter_for_mitarbeiter: mid, only_implemented: true }
            : { filter_by_role: true, only_implemented: true };
        const data = await benachrichtigungenService.listEvents(params);
        setNotificationEvents(data);
      } catch {
        setNotificationEvents([]);
      }
    };
    loadEvents();
  }, [id, canEditBenachrichtigungen]);

  useEffect(() => {
    if (!isNew && id && canEditBenachrichtigungen) {
      benachrichtigungenService
        .getByMitarbeiter(parseInt(id))
        .then((data) => {
          setBenachrichtigungen(data);
          setGotifyUrl(data.gotify_url ?? data.gotify_url_effective ?? '');
          setOverrideEventsEmail(data.admin_override_events_email ?? data.events_email ?? []);
          setOverrideEventsGotify(data.admin_override_events_gotify ?? data.events_gotify ?? []);
        })
        .catch(() => setBenachrichtigungen(null));
    }
  }, [id, isNew, canEditBenachrichtigungen]);

  const handleOverrideEventEmailToggle = (eventId: string) => {
    setOverrideEventsEmail((prev) =>
      prev.includes(eventId) ? prev.filter((e) => e !== eventId) : [...prev, eventId]
    );
  };

  const handleOverrideEventGotifyToggle = (eventId: string) => {
    setOverrideEventsGotify((prev) =>
      prev.includes(eventId) ? prev.filter((e) => e !== eventId) : [...prev, eventId]
    );
  };

  const handleSaveBenachrichtigungen = async () => {
    if (!id || isNew) return;
    setBenachrichtigungenSaving(true);
    try {
      const data: BenachrichtigungenUpdateAdmin = {};
      if (gotifyToken) data.gotify_token = gotifyToken;
      data.gotify_url = gotifyUrl || undefined;
      const updated = await benachrichtigungenService.updateByMitarbeiter(parseInt(id), data);
      setBenachrichtigungen(updated);
      setGotifyToken('');
      setGotifyUrl(updated.gotify_url || updated.gotify_url_effective || '');
      showSuccess('Benachrichtigungs-Einstellungen gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setBenachrichtigungenSaving(false);
    }
  };

  const handleRemoveGotifyToken = async () => {
    if (!id || isNew) return;
    setBenachrichtigungenSaving(true);
    try {
      const updated = await benachrichtigungenService.updateByMitarbeiter(parseInt(id), {
        gotify_token: '',
      });
      setBenachrichtigungen(updated);
      setGotifyToken('');
      showSuccess('Gotify-Token entfernt');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Entfernen');
    } finally {
      setBenachrichtigungenSaving(false);
    }
  };

  const handleResetOverride = async () => {
    if (!id || isNew) return;
    setBenachrichtigungenSaving(true);
    try {
      const updated = await benachrichtigungenService.updateByMitarbeiter(parseInt(id), {
        reset_override: true,
      });
      setBenachrichtigungen(updated);
      setOverrideEventsEmail(updated.events_email ?? []);
      setOverrideEventsGotify(updated.events_gotify ?? []);
      showSuccess('Override zurückgesetzt – Nutzer-Einstellungen gelten wieder');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setBenachrichtigungenSaving(false);
    }
  };

  const handleOverrideAllEmail = (activate: boolean) => {
    const eventIds = notificationEvents.map((e) => e.id);
    setOverrideEventsEmail(activate ? eventIds : []);
  };

  const handleOverrideAllGotify = (activate: boolean) => {
    const eventIds = notificationEvents.map((e) => e.id);
    setOverrideEventsGotify(activate ? eventIds : []);
  };

  const handleSaveOverride = async () => {
    if (!id || isNew) return;
    setBenachrichtigungenSaving(true);
    try {
      const updated = await benachrichtigungenService.updateByMitarbeiter(parseInt(id), {
        admin_override_events_email: overrideEventsEmail,
        admin_override_events_gotify: overrideEventsGotify,
      });
      setBenachrichtigungen(updated);
      showSuccess('Event-Override gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setBenachrichtigungenSaving(false);
    }
  };

  const handleInputChange = (field: keyof MitarbeiterCreate) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSelectChange = (field: keyof MitarbeiterCreate) => (
    e: any
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSwitchChange = (field: keyof MitarbeiterCreate) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  const handleSave = async () => {
    if (!isNew && formData.password && !passwordPolicy.valid) {
      setError(`Passwort-Richtlinie verletzt: ${passwordPolicy.errors.join('; ')}`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const dataToSend: any = { ...formData };
      if (!dataToSend.eintrittsdatum) {
        dataToSend.eintrittsdatum = null;
      }
      if (!isNew && !dataToSend.password) {
        delete dataToSend.password;
      }
      if (isNew) {
        delete dataToSend.password;
      }

      if (isNew) {
        await mitarbeiterService.create(dataToSend);
        showSuccess('Mitarbeiter erstellt – Willkommens-E-Mail wurde versendet');
      } else if (id) {
        await mitarbeiterService.update(parseInt(id), dataToSend);
        showSuccess('Mitarbeiter erfolgreich aktualisiert');
      }
      setTimeout(() => {
        navigate('/mitarbeiter');
      }, 1000);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleReset2FA = async () => {
    if (!id || isNew) return;
    setReset2faLoading(true);
    try {
      await mitarbeiterService.reset2FA(parseInt(id));
      showSuccess('2FA erfolgreich zurückgesetzt');
      setMitarbeiter((prev) => prev ? { ...prev, has_2fa: false } : prev);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Zurücksetzen der 2FA');
    } finally {
      setReset2faLoading(false);
      setReset2faDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      await mitarbeiterService.delete(parseInt(id));
      showSuccess('Mitarbeiter erfolgreich gelöscht');
      setTimeout(() => {
        navigate('/mitarbeiter');
      }, 1000);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleForcePasswordReset = async () => {
    if (!id || isNew) return;
    setForcePasswordResetLoading(true);
    try {
      const response = await mitarbeiterService.forcePasswordReset(parseInt(id));
      showSuccess(response?.message || 'Passwort-Reset wurde erzwungen und E-Mail wurde versendet');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Erzwingen des Passwort-Resets');
    } finally {
      setForcePasswordResetLoading(false);
      setForcePasswordResetDialogOpen(false);
    }
  };

  if (loading) {
    return <FormSkeleton fields={5} />;
  }

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Neuer Mitarbeiter' : mitarbeiter ? `${mitarbeiter.vorname} ${mitarbeiter.nachname}` : 'Mitarbeiter'}
        breadcrumbs={[
          { label: 'Mitarbeiter', path: '/mitarbeiter' },
          { label: isNew ? 'Neu' : mitarbeiter ? `${mitarbeiter.vorname} ${mitarbeiter.nachname}` : 'Mitarbeiter' },
        ]}
      />

      {!isNew && id && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Änderungshistorie">
            <IconButton onClick={() => setHistorieOpen(true)}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vorname"
                value={formData.vorname}
                onChange={handleInputChange('vorname')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nachname"
                value={formData.nachname}
                onChange={handleInputChange('nachname')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rollen</InputLabel>
                <Select
                  multiple
                  value={formData.rollen_ids || []}
                  onChange={(e) =>
                    setFormData({ ...formData, rollen_ids: e.target.value as number[] })
                  }
                  input={<OutlinedInput label="Rollen" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((value) => {
                        const rolle = rollen.find((r) => r.id === value);
                        return (
                          <Chip
                            key={value}
                            label={rolle?.name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {rollen.map((rolle) => (
                    <MenuItem key={rolle.id} value={rolle.id}>
                      <Checkbox checked={(formData.rollen_ids || []).includes(rolle.id)} />
                      <ListItemText primary={rolle.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Urlaubstage pro Jahr"
                type="number"
                value={formData.urlaubstage_gesamt ?? 30}
                onChange={(e) => setFormData({ ...formData, urlaubstage_gesamt: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 365 }}
                helperText="Volles Jahreskontingent — im Eintrittsjahr anteilig: 1/12 pro vollem Monat (Eintritt bis zum 15. zählt mit)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Eintrittsdatum"
                type="date"
                value={formData.eintrittsdatum || ''}
                onChange={(e) => setFormData({ ...formData, eintrittsdatum: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Beginn der Beschäftigung — ab dem 16. zählt der Eintrittsmonat nicht als voller Monat"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Urlaubsübertrag"
                type="number"
                value={formData.urlaub_uebertrag ?? 0}
                onChange={(e) => setFormData({ ...formData, urlaub_uebertrag: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 365, step: 0.5 }}
                helperText="Zusätzliche manuelle Korrektur. Resturlaub aus dem Vorjahr wird automatisch übertragen (siehe Systemkonfiguration → Urlaub)."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.aktiv ?? true}
                    onChange={handleSwitchChange('aktiv')}
                  />
                }
                label="Aktiv"
                sx={{ mt: 1 }}
              />
            </Grid>
            {!isNew && mitarbeiter && (
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
                  border: `1px solid ${theme.palette.divider}`,
                  flexWrap: 'wrap',
                }}>
                  <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Zwei-Faktor-Authentifizierung
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {mitarbeiter.has_2fa ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="2FA aktiv"
                          size="small"
                          sx={{
                            bgcolor: isDark ? '#1a3a2a' : '#e8f5e9',
                            color: accent,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: accent },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="2FA inaktiv"
                          size="small"
                          sx={{
                            bgcolor: isDark ? '#3a2020' : '#fbe9e7',
                            color: '#ef5350',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#ef5350' },
                          }}
                        />
                      )}
                      {mitarbeiter.mfa_pflicht && (
                        <Chip
                          label="Erzwungen"
                          size="small"
                          sx={{
                            bgcolor: isDark ? '#2c2c40' : '#e3f2fd',
                            color: isDark ? '#90caf9' : '#1565c0',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mitarbeiter.mfa_pflicht}
                          onChange={async (e) => {
                            try {
                              const updated = await mitarbeiterService.update(parseInt(id!), { mfa_pflicht: e.target.checked });
                              setMitarbeiter((prev) => prev ? { ...prev, mfa_pflicht: updated.mfa_pflicht } : prev);
                              showSuccess(e.target.checked ? '2FA wird beim nächsten Login erzwungen' : '2FA-Pflicht aufgehoben');
                            } catch (err: any) {
                              setError(err.response?.data?.detail || 'Fehler beim Ändern der 2FA-Pflicht');
                            }
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>2FA erzwingen</Typography>}
                    />
                    {mitarbeiter.has_2fa && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setReset2faDialogOpen(true)}
                        disabled={reset2faLoading}
                      >
                        {reset2faLoading ? <CircularProgress size={18} /> : 'Zurücksetzen'}
                      </Button>
                    )}
                    {hasRole('Admin') && (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => setForcePasswordResetDialogOpen(true)}
                        disabled={forcePasswordResetLoading}
                      >
                        {forcePasswordResetLoading ? <CircularProgress size={18} /> : 'PW-Reset erzwingen'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
            {!isNew && hasRole('Admin') && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
                    border: `1px solid ${theme.palette.divider}`,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Bleeding Edge
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Schaltet experimentelle Features frei, z. B. den AI-Assistant in der Schwachstellendetailseite.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(formData.bleeding_edge)}
                        onChange={handleSwitchChange('bleeding_edge' as keyof MitarbeiterCreate)}
                      />
                    )}
                    label="Experimentelle Features aktivieren"
                    sx={{ mr: 0 }}
                  />
                </Box>
              </Grid>
            )}
            {!isNew && canEditBenachrichtigungen && (
              <Grid item xs={12}>
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
                  border: `1px solid ${theme.palette.divider}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <NotificationsActiveIcon sx={{ color: accent }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Benachrichtigungen (Gotify)
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Token und URL werden vom Admin in Gotify angelegt und hier eingetragen.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Gotify Token"
                        type="password"
                        value={gotifyToken}
                        onChange={(e) => setGotifyToken(e.target.value)}
                        placeholder={benachrichtigungen?.gotify_token_set ? '•••••••• (bereits gesetzt)' : ''}
                        helperText="Leer lassen, um den bestehenden Token beizubehalten"
                      />
                      {benachrichtigungen?.gotify_token_set && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleRemoveGotifyToken}
                          disabled={benachrichtigungenSaving}
                          sx={{ mt: 1, flexShrink: 0 }}
                        >
                          Entfernen
                        </Button>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Gotify URL"
                      value={gotifyUrl}
                      onChange={(e) => setGotifyUrl(e.target.value)}
                      placeholder="https://push.aio.stark-its.de"
                      helperText={
                        (() => {
                          if (benachrichtigungen?.gotify_url_from_app_settings) {
                            return `Aktuell aus Systemkonfiguration (globale URL): ${benachrichtigungen.gotify_url_effective || '—'}`;
                          }
                          if (benachrichtigungen?.gotify_url_from_env) {
                            return `Aktuell aus Umgebungsvariable GOTIFY_URL: ${benachrichtigungen.gotify_url_effective || '—'}`;
                          }
                          return 'Leer lassen für globale Vorgabe: Systemkonfiguration („Gotify (Push)“), sonst GOTIFY_URL auf dem Server';
                        })()
                      }
                    />
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Event-Override {(benachrichtigungen?.admin_override_events_email !== null || benachrichtigungen?.admin_override_events_gotify !== null) && '(überschreibt Nutzer-Einstellungen)'} – E-Mail: {overrideEventsEmail.length} / Gotify: {overrideEventsGotify.length} aktiviert
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        {(benachrichtigungen?.admin_override_events_email !== null || benachrichtigungen?.admin_override_events_gotify !== null)
                          ? 'Aktive Events für diesen Nutzer (pro Kanal):'
                          : 'Nutzer-Einstellungen gelten. Wählen Sie Events pro Kanal, um sie zu überschreiben:'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>E-Mail:</Typography>
                          <Button variant="outlined" size="small" onClick={() => handleOverrideAllEmail(false)} disabled={overrideEventsEmail.length === 0}>
                            Alle ab
                          </Button>
                          <Button variant="outlined" size="small" onClick={() => handleOverrideAllEmail(true)} disabled={overrideEventsEmail.length === notificationEvents.length}>
                            Alle an
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>Gotify:</Typography>
                          <Button variant="outlined" size="small" onClick={() => handleOverrideAllGotify(false)} disabled={overrideEventsGotify.length === 0}>
                            Alle ab
                          </Button>
                          <Button variant="outlined" size="small" onClick={() => handleOverrideAllGotify(true)} disabled={overrideEventsGotify.length === notificationEvents.length}>
                            Alle an
                          </Button>
                        </Box>
                        {(benachrichtigungen?.admin_override_events_email !== null || benachrichtigungen?.admin_override_events_gotify !== null) && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={handleResetOverride}
                            disabled={benachrichtigungenSaving}
                          >
                            Override zurücksetzen
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Ereignis</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Gotify</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(
                              notificationEvents.reduce<Record<string, EventDefinition[]>>((acc, ev) => {
                                if (!acc[ev.category]) acc[ev.category] = [];
                                acc[ev.category].push(ev);
                                return acc;
                              }, {})
                            ).flatMap(([category, evs]) => [
                              <TableRow key={`cat-${category}`}>
                                <TableCell colSpan={3} sx={{ pt: 1.5, pb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
                                  {category}
                                </TableCell>
                              </TableRow>,
                              ...evs.map((ev) => (
                                <TableRow key={ev.id}>
                                  <TableCell sx={{ pl: 3 }}>{ev.label}</TableCell>
                                  <TableCell align="center">
                                    <Checkbox
                                      size="small"
                                      checked={overrideEventsEmail.includes(ev.id)}
                                      onChange={() => handleOverrideEventEmailToggle(ev.id)}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Checkbox
                                      size="small"
                                      checked={overrideEventsGotify.includes(ev.id)}
                                      onChange={() => handleOverrideEventGotifyToggle(ev.id)}
                                    />
                                  </TableCell>
                                </TableRow>
                              )),
                            ])}
                          </TableBody>
                        </Table>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSaveOverride}
                        disabled={benachrichtigungenSaving}
                        sx={{ mt: 1 }}
                      >
                        {benachrichtigungenSaving ? <CircularProgress size={18} /> : 'Override speichern'}
                      </Button>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveBenachrichtigungen}
                      disabled={
                        benachrichtigungenSaving ||
                        (gotifyToken === '' && gotifyUrl === (benachrichtigungen?.gotify_url ?? benachrichtigungen?.gotify_url_effective ?? ''))
                      }
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {benachrichtigungenSaving ? <CircularProgress size={20} /> : 'Speichern'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
            {isNew && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Der neue Mitarbeiter erhaelt automatisch eine Willkommens-E-Mail mit einem Link zum Setzen seines Passworts.
                </Alert>
              </Grid>
            )}
            {!isNew && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Passwort ändern"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  error={Boolean(formData.password) && !passwordPolicy.valid}
                  helperText={formData.password ? 'Policy wird unten live geprüft' : 'Leer lassen, um das Passwort nicht zu ändern'}
                />
                {formData.password && (
                  <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.5, mt: 1 }}>
                    {passwordPolicy.checks.map((check) => (
                      <Typography
                        key={check.key}
                        variant="caption"
                        sx={{ display: 'block', color: check.passed ? 'success.main' : 'error.main' }}
                      >
                        {check.passed ? 'OK ' : 'X '} {check.label}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !formData.vorname || !formData.nachname || !formData.email}
                >
                  {saving ? <CircularProgress size={24} /> : 'Speichern'}
                </Button>
                {!isNew && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Löschen
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!isNew && mitarbeiter?.profil && (
        <MitarbeiterProfilReadOnlyCard profil={mitarbeiter.profil} />
      )}

      {!isNew && id && (
        <HistorieTimeline
          entitaetTyp="Mitarbeiter"
          entitaetId={parseInt(id)}
          open={historieOpen}
          onClose={() => setHistorieOpen(false)}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Mitarbeiter löschen"
        message="Möchten Sie diesen Mitarbeiter wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <ConfirmDialog
        open={reset2faDialogOpen}
        title="2FA zurücksetzen"
        message={`Möchten Sie die Zwei-Faktor-Authentifizierung für ${mitarbeiter?.vorname} ${mitarbeiter?.nachname} wirklich deaktivieren?`}
        onConfirm={handleReset2FA}
        onCancel={() => setReset2faDialogOpen(false)}
        confirmText="Zurücksetzen"
        cancelText="Abbrechen"
      />

      <ConfirmDialog
        open={forcePasswordResetDialogOpen}
        title="Passwort-Reset erzwingen"
        message={`Möchten Sie für ${mitarbeiter?.vorname} ${mitarbeiter?.nachname} einen Passwort-Reset erzwingen? Der Benutzer erhält eine E-Mail und muss ein neues Passwort setzen.`}
        onConfirm={handleForcePasswordReset}
        onCancel={() => setForcePasswordResetDialogOpen(false)}
        confirmText="Reset erzwingen"
        cancelText="Abbrechen"
      />
    </Box>
  );
};

export default MitarbeiterDetailPage;
